import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Namespace, useT } from "@racoon/i18n";
import { Button } from "@racoon/ui";
import { IcNavigation } from "@racoon/icons";
import { useVirtualizer } from "@tanstack/react-virtual";
import { CardContentWrapper } from "~/ui/components/cardContent.wrapper";
import { InfoRow } from "~/ui/components/infoRow";
import { ViewSelector } from "../../../shared/components/viewSelector";
import { DateView, getLastSevenDaysDates } from "./dateView";
import { StatCard } from "./statCard";
import { useTruckAnalyticsByIdQuery, useTruckByIdQuery } from "~/shared/api/govDash/govDash.api";
import {
  formatTimestampToDate,
  toLocalWallClockTimestamp,
} from "~/shared/utils/filterDateFormatter";
import { gpsTrackingUrl } from "~/pages/gps-tracking/model/url-state";
import { useCanEditGovDash } from "~/shared/hooks/useCanEditGovDash";

type TruckDetailsSection = "general" | "summary" | "visits";

export const TruckDetails = ({ selectedRecordId }: { selectedRecordId: string | undefined }) => {
  const { data: truckData } = useTruckByIdQuery({
    id: selectedRecordId ? BigInt(selectedRecordId) : undefined,
  });

  const t = useT();
  const canEditGovDash = useCanEditGovDash();
  const { weekStart: initialWeekStart, weekEnd: initialWeekEnd } = getLastSevenDaysDates();
  const [weekStart, setWeekStart] = useState(initialWeekStart);
  const [weekEnd, setWeekEnd] = useState(initialWeekEnd);
  const stopsListRef = useRef<HTMLDivElement | null>(null);
  const { data: truckAnalyticsData } = useTruckAnalyticsByIdQuery(
    {
      truckId: selectedRecordId ? BigInt(selectedRecordId) : undefined,
      fromTo: {
        from: toLocalWallClockTimestamp(weekStart),
        to: toLocalWallClockTimestamp(weekEnd),
      },
    },
    {
      skip: !selectedRecordId,
    },
  );
  const gpsStops = truckAnalyticsData?.gpsStops ?? [];
  const gpsStopsVirtualizer = useVirtualizer({
    count: gpsStops.length,
    getScrollElement: () => stopsListRef.current,
    estimateSize: () => 80,
    overscan: 6,
  });

  const viewOptions = useMemo(
    () =>
      [
        { value: "general" as const, label: t(Namespace.Govdash, "general") },
        { value: "summary" as const, label: t(Namespace.Govdash, "summary") },
        { value: "visits" as const, label: t(Namespace.Govdash, "visitsTitle") },
      ] as const,
    [t],
  );

  const [activeSection, setActiveSection] = useState<TruckDetailsSection>("general");

  const generalRows = useMemo(
    () => [
      {
        label: t(Namespace.Govdash, "plateNumber"),
        value: truckData?.truck?.truck?.plateNumber || "-",
      },
      { label: t(Namespace.Govdash, "vehicleModel"), value: truckData?.truck?.truck?.model || "-" },
      {
        label: t(Namespace.Govdash, "subtitle.serviceProvider"),
        value: truckData?.truck?.serviceProvider?.name || "-",
      },
      { label: t(Namespace.Govdash, "subtitle.depot"), value: truckData?.depot?.name || "-" },
      {
        label: t(Namespace.Govdash, "geolocationDepot"),
        value: (
          <a
            className="block max-w-xs min-w-20 truncate"
            href={`https://2gis.kz/geo/${truckData?.depot?.lon},${truckData?.depot?.lat}`}
            target="_blank"
            rel="noreferrer"
          >
            https://2gis.kz/geo/{truckData?.depot?.lon},{truckData?.depot?.lat}
          </a>
        ),
      },
      // { label: t(Namespace.Govdash, "servingArea"), value: "Bostandyk district" },
      // {
      //   label: t(Namespace.Govdash, "status.onLine"),
      //   value: truckData?.truck?.serviceProvider?.name || "-",
      // },
    ],
    [t, truckData],
  );

  return (
    <CardContentWrapper
      hasDivider={activeSection !== "general"}
      className="flex min-h-0 flex-col overflow-hidden pb-2"
    >
      <div className="shrink-0 p-2">
        <ViewSelector
          options={viewOptions}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
      </div>
      {activeSection === "general" && (
        <>
          <div className="p-4 space-y-4">
            <CardContentWrapper>
              {generalRows.map((row) => (
                <InfoRow key={row.label} label={row.label} value={row.value} />
              ))}
            </CardContentWrapper>
            {/* Today's track of this truck; the screen is desktop only, for editors. */}
            {selectedRecordId && canEditGovDash && (
              <div className="hidden lg:block">
                <Button asChild variant="opacityBlueWithOutline" className="w-full gap-2">
                  <Link
                    to={gpsTrackingUrl({ truckId: selectedRecordId })}
                    data-testid="truck-details-gps-tracking"
                  >
                    <IcNavigation className="h-5 w-5" />
                    {t(Namespace.Govdash, "gpsTracking.openTrack")}
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </>
      )}
      {activeSection === "summary" && (
        <>
          <DateView
            weekStart={weekStart}
            weekEnd={weekEnd}
            onDateChange={(from, to) => {
              if (from) setWeekStart(from);
              if (to) setWeekEnd(to);
            }}
          />
          {/* TODO: when other details will be added adjust grid-cols-X based on how many details there are */}
          <div className="grid grid-cols-2 gap-2 p-4">
            <StatCard
              title={t(Namespace.Govdash, "pickupLocationVisits")}
              value={truckAnalyticsData?.totalLocationStops?.toString() ?? "0"}
            />
            {/* <StatCard title={t(Namespace.Govdash, "wasteVolume")} value={891} /> */}
            {/* <StatCard title={t(Namespace.Govdash, "mileage")} value={450} /> */}
            <StatCard
              title={t(Namespace.Govdash, "wasteSortingPlantVisits")}
              value={truckAnalyticsData?.disposalStops?.toString() ?? "0"}
            />
            {/* <StatCard title={t(Namespace.Govdash, "wasteTonnage")} value={13.2} /> */}
            {/* <StatCard title={t(Namespace.Govdash, "anomalies")} value={5} /> */}
          </div>
        </>
      )}
      {activeSection === "visits" && (
        <>
          <div className="shrink-0">
            <DateView
              weekStart={weekStart}
              weekEnd={weekEnd}
              onDateChange={(from, to) => {
                if (from) setWeekStart(from);
                if (to) setWeekEnd(to);
              }}
            />
          </div>
          {gpsStops.length > 0 ? (
            <div ref={stopsListRef} className="min-h-0 flex-1 overflow-y-auto">
              <div
                role="list"
                className="relative w-full"
                style={{ height: `${gpsStopsVirtualizer.getTotalSize()}px` }}
              >
                {gpsStopsVirtualizer.getVirtualItems().map((virtualRow) => {
                  const visit = gpsStops[virtualRow.index];
                  return (
                    <div
                      role="listitem"
                      aria-setsize={gpsStops.length}
                      aria-posinset={virtualRow.index + 1}
                      key={visit.id}
                      data-index={virtualRow.index}
                      ref={gpsStopsVirtualizer.measureElement}
                      className="absolute left-0 top-0 w-full"
                      style={{ transform: `translateY(${virtualRow.start}px)` }}
                    >
                      <div className="flex w-full items-stretch gap-3 border-b border-slate-200 bg-white px-3 py-3 text-left transition-colors hover:bg-slate-50 active:bg-slate-100">
                        <div className="flex shrink-0 items-start justify-center pt-0.5">
                          <span className="text-xs/4 font-bold leading-none tracking-tight text-slate-900">
                            {visit.id.toString()}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 space-y-2">
                          <p className="text-xs/4 font-semibold uppercase tracking-tight text-slate-900">
                            {visit.pickupLocation?.name || visit.location?.name || "--"}
                          </p>
                          <div className="flex justify-between items-center gap-3 text-sm font-normal leading-4 tracking-tight text-text-secondary">
                            <p>{visit.truck?.plateNumber}</p>
                            {/* <span>{visit.volume}</span> */}
                            <span>{formatTimestampToDate(visit.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 items-center justify-center py-8">
              <p className="text-sm text-text-secondary">{t(Namespace.Trucks, "tableNoData")}</p>
            </div>
          )}
        </>
      )}
    </CardContentWrapper>
  );
};
